/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { alpha, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { getRaisedSurfaceColor, popoverBorderRadius } from '../../styles';

export const ROW_ACTIONS_CLASS = 'pa-notification-row-actions';
export const ROW_TIME_CLASS = 'pa-notification-row-time';

const getRowBackground = (
  theme: Theme,
  { unread, selected }: { unread: boolean; selected: boolean },
) => {
  const dark = theme.palette.mode === 'dark';
  if (selected) return alpha(theme.palette.primary.main, dark ? 0.2 : 0.12);
  if (unread) return alpha(theme.palette.primary.main, dark ? 0.12 : 0.07);
  return 'transparent';
};

export const getRowSx =
  ({ unread, selected }: { unread: boolean; selected: boolean }) =>
  (theme: Theme) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    width: '100%',
    p: '10px 12px 10px 10px',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: selected ? alpha(theme.palette.primary.main, 0.55) : 'transparent',
    bgcolor: getRowBackground(theme, { unread, selected }),
    cursor: 'pointer',
    textAlign: 'left',
    color: 'inherit',
    font: 'inherit',
    [`& .${ROW_ACTIONS_CLASS}`]: { display: 'none' },
    '&:hover, &:focus-within': {
      bgcolor: selected ? undefined : theme.palette.action.hover,
      [`& .${ROW_ACTIONS_CLASS}`]: { display: 'flex' },
      [`& .${ROW_TIME_CLASS}`]: { display: 'none' },
    },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  });

export const lineClampSx = (lines: number): SystemStyleObject<Theme> => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden',
});

export const getGlyphTileSx = (size: number) => (theme: Theme) => ({
  position: 'relative' as const,
  flexShrink: 0,
  width: size,
  height: size,
  display: 'grid',
  placeItems: 'center',
  borderRadius: `${Math.round(size * 0.28)}px`,
  bgcolor: getRaisedSurfaceColor(theme),
  border: `1px solid ${theme.palette.divider}`,
  color: 'text.primary',
});

export const getMessageQuoteSx = (color: string) => ({
  borderLeft: '2px solid',
  borderColor: 'divider',
  pl: 1,
  color,
  fontWeight: 500,
});

export const dayHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  px: 1.25,
  pt: 1,
  pb: 0.5,
  color: 'text.secondary',
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  '&::after': { content: '""', flex: 1, height: '1px', bgcolor: 'divider' },
} as const;

// Same surface recipe as the other v4 shell popovers: translucent paper, blur, soft shadow.
export const getFloatingSurfaceSx = (theme: Theme) => ({
  bgcolor: alpha(theme.palette.background.paper, 0.92),
  backgroundImage: 'none',
  backdropFilter: 'blur(28px) saturate(160%)',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: `${popoverBorderRadius}px`,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 22px 52px rgba(0, 0, 0, 0.45)'
      : '0 22px 52px rgba(23, 49, 69, 0.18)',
  overflow: 'hidden',
});
